/**
 * The live stream from the capture engine — one connection for the whole app.
 *
 * `useSSE` used to open its own `EventSource` per call site, and the radar had
 * two mounted at once (the page shell and the radar inside it), so a single
 * screen held two connections to the same endpoint and every group message
 * arrived twice. The socket is opened once here and fanned out to subscribers.
 *
 * The coalescing is unchanged and still matters: the engine broadcasts on
 * *every* captured message, and on a busy morning that is several per second.
 * A plain trailing debounce either fires constantly or, under a continuous
 * stream, keeps resetting and never fires at all. So this is a debounce with a
 * max wait — bursts collapse into one call, and a sustained stream still
 * refreshes on a bounded cadence instead of thrashing.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from "react";

type Listener = () => void;

interface LiveApi {
  subscribe: (listener: Listener) => () => void;
}

const LiveContext = createContext<LiveApi | null>(null);

export function LiveProvider({ children }: { children: ReactNode }) {
  const listeners = useRef(new Set<Listener>());

  useEffect(() => {
    const source = new EventSource("/api/wa-engine/events", { withCredentials: true });
    const fan = () => listeners.current.forEach((listener) => listener());
    source.addEventListener("update", fan);
    // EventSource reconnects on its own; swallowing the error keeps a restart
    // of the server from filling the console during development.
    source.onerror = () => {};
    return () => source.close();
  }, []);

  const api = useMemo<LiveApi>(() => ({
    subscribe: (listener) => {
      listeners.current.add(listener);
      return () => { listeners.current.delete(listener); };
    },
  }), []);

  return <LiveContext.Provider value={api}>{children}</LiveContext.Provider>;
}

/**
 * Run `callback` when the engine reports a change.
 *
 * `maxWaitMs` is the floor on how often a heavy refetch can run. The radar
 * reloads 500 listings, so it asks for a wide one; a status badge can be tight.
 */
export function useLive(callback: () => void, debounceMs = 300, maxWaitMs = 4000): void {
  const live = useContext(LiveContext);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const subscribe = live?.subscribe;

  const run = useCallback(() => callbackRef.current(), []);

  useEffect(() => {
    // No provider (a public page such as /login) — nothing to listen to.
    if (!subscribe) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let burstStartedAt: number | null = null;

    const fire = () => { burstStartedAt = null; timer = null; run(); };

    const unsubscribe = subscribe(() => {
      const now = Date.now();
      if (burstStartedAt == null) burstStartedAt = now;
      if (timer) clearTimeout(timer);
      // Fire `debounceMs` after the last event, but never later than
      // `maxWaitMs` after the first event of the current burst.
      const wait = Math.min(debounceMs, Math.max(0, maxWaitMs - (now - burstStartedAt)));
      timer = setTimeout(fire, wait);
    });

    return () => {
      unsubscribe();
      if (timer) clearTimeout(timer);
    };
  }, [subscribe, debounceMs, maxWaitMs, run]);
}
